import { GithubBackupSection } from './GithubBackupSection';
import { ImportFromGithubSection } from './ImportFromGithubSection';
import { EmailSection } from './EmailSection';
import { HolidaySection } from './HolidaySection';
import { DisplayTemplatesSection } from './DisplayTemplatesSection';
import SourceCodesSection from './SourceCodesSection';
import { AutoExportSection } from './AutoExportSection';
import { OperatorRulesSection } from './OperatorRulesSection';
import { useAuth } from '../../../hooks/useAuth';
import { useCurrentTenant } from '../../../hooks/useCurrentTenant';
import { useJobTypes } from '../hooks/useJobTypes';
import { useExportFormats } from '../hooks/useExportFormats';
import { CopyButton } from '../../../components/CopyButton';
import { api } from '../../../lib/api-client';
import { showApiError } from '../../../lib/error-toast';
import styles from './tenant-settings.module.css';

export function TenantSettingsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { data: tenant } = useCurrentTenant();
  const { items: jobTypes, isLoading: jtLoading } = useJobTypes();
  const { formats: exportFormats, isLoading: efLoading } = useExportFormats();

  const loading = authLoading || jtLoading || efLoading;
  const suiteUrl =
    (typeof window !== 'undefined' &&
      (window as unknown as { VITE_SUITE_URL?: string }).VITE_SUITE_URL) ||
    'https://suite.stratummanager.com';

  return (
    <div className={`${styles.page} form-compact`}>
      <h1 className={styles.pageTitle}>Tenant Settings</h1>

      {/* ── Overview ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Overview</h2>

        <div className={styles.overviewGrid}>
          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Tenant Name</span>
            <span className={styles.overviewValue}>
              {tenant?.name ?? '—'}
            </span>
            <div className={styles.overviewHint}>
              Set in Skarn Suite. Contact your administrator to change.
            </div>
          </div>

          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Tenant ID</span>
            <div className={styles.tenantIdRow}>
              <span className={`${styles.overviewValue} ${styles.tenantIdValue}`}>
                {loading ? '—' : user?.tenantId ?? '—'}
              </span>
              {!loading && user?.tenantId && (
                <CopyButton value={user.tenantId} label="tenant ID" />
              )}
            </div>
            <div className={styles.overviewHint}>
              Tenant identity is managed by the Skarn Suite.{' '}
              <a
                className={styles.suiteLink}
                href={suiteUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open Suite →
              </a>
            </div>
          </div>

          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Signed-in admin</span>
            <span className={styles.overviewValue}>
              {loading ? '—' : user?.name ?? '—'}
            </span>
            <div className={styles.overviewHint}>
              {loading ? '' : user?.email ?? ''}
            </div>
          </div>

          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Subscription</span>
            <span className={styles.overviewValue}>Managed by Suite</span>
            <div className={styles.overviewHint}>
              Billing, seats, and plan changes happen in{' '}
              <a
                className={styles.suiteLink}
                href={`${suiteUrl.replace(/\/$/, '')}/billing`}
                target="_blank"
                rel="noreferrer noopener"
              >
                Suite → Billing
              </a>
              .
            </div>
          </div>
        </div>
      </section>

      {/* ── Configured so far ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Configured so far</h2>

        <div className={styles.overviewGrid}>
          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Job Types</span>
            <span className={styles.overviewValue}>
              {loading ? '—' : jobTypes.length}
            </span>
            <div className={styles.overviewHint}>
              Document categories with pipelines + field definitions.
            </div>
          </div>

          <div className={styles.overviewCard}>
            <span className={styles.overviewLabel}>Export Formats</span>
            <span className={styles.overviewValue}>
              {loading ? '—' : exportFormats.length}
            </span>
            <div className={styles.overviewHint}>
              CSV, fixed-width, XML, or JSON targets configured for export.
            </div>
          </div>
        </div>
      </section>

      {/* ── Display Names (R-146) ── */}
      <DisplayTemplatesSection />

      {/* ── Source Codes (R-167) — channel→code mapping consumed by {sourceCode}. */}
      <SourceCodesSection
        initialChannelCodes={(tenant?.channelSourceCodes ?? null) as Record<string, string> | null}
        initialDeviceCodes={(tenant?.scannerDeviceCodes ?? null) as Record<string, string> | null}
        onSave={(payload) =>
          // R-180 — return the promise so SourceCodesSection can drive its
          // saving spinner + "Saved" indicator off the round-trip. The catch
          // keeps the resolution (no rethrow) so a failed save still settles
          // the button rather than leaving it stuck on "Saving…".
          api
            .patch('/me/tenant', payload)
            .catch((e) => showApiError(e, { fallback: 'Failed to save source codes' }))
        }
      />

      {/* ── Auto-export release (R-155) ── */}
      <AutoExportSection />

      {/* F-065 — operator-visible automation guide (tenant opt-in, default off). */}
      <OperatorRulesSection />

      {/* ── Backup ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Configuration backup</h2>
        <GithubBackupSection />
      </section>

      {/* ── Import from GitHub (S-6) ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Import from GitHub</h2>
        <ImportFromGithubSection />
      </section>

      {/* ── Email notifications (S-1) ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Email notifications</h2>
        <EmailSection />
      </section>

      {/* ── Holidays (S-4) ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>Holidays</h2>
        <HolidaySection />
      </section>
    </div>
  );
}
